// ============================================================
// Expense Category Page (/expenses/categories)
// ============================================================

import { useState } from 'react';
import { Box, Typography, Button, Stack, IconButton, TextField, InputAdornment, Tooltip, Chip, Card, CardContent, Grid } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Search, Edit, Trash2, Tag, CheckCircle2, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { ExpenseCategoryModal } from './components/ExpenseCategoryModal';
import { useExpenseCategories, useCreateExpenseCategory, useUpdateExpenseCategory, useDeleteExpenseCategory } from '@/hooks/useExpenses';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import toast from 'react-hot-toast';

export function ExpenseCategoryPage() {
  usePageMeta('Expense Categories | Expense Management', [
    { label: 'Dashboard', path: '/' },
    { label: 'Expenses', path: '/expenses' },
    { label: 'Categories' },
  ]);

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);

  // Queries & Mutations
  const { data: categories = [], isLoading, error, refetch } = useExpenseCategories(true);
  const createMutation = useCreateExpenseCategory();
  const updateMutation = useUpdateExpenseCategory();
  const deleteMutation = useDeleteExpenseCategory();

  const filteredCategories = (categories || []).filter((cat) => {
    if (!search.trim()) return true;
    return (cat.name || '').toLowerCase().includes(search.toLowerCase());
  });

  const handleOpenAdd = () => {
    setEditingCategory(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (cat) => {
    setEditingCategory(cat);
    setModalOpen(true);
  };

  const handleSubmitModal = async (payload) => {
    try {
      if (editingCategory) {
        await updateMutation.mutateAsync({ id: editingCategory.id, payload });
        toast.success('Expense category updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Expense category created successfully');
      }
      setModalOpen(false);
      setEditingCategory(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save expense category');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCategory) return;
    try {
      await deleteMutation.mutateAsync(deleteCategory.id);
      toast.success('Expense category deleted successfully');
      setDeleteCategory(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete expense category');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Category Name',
      flex: 2,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: tokens.radius.sm,
              backgroundColor: tokens.colors.primary[50],
              color: tokens.colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tag size={16} />
          </Box>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const active = Boolean(params.value);
        return (
          <Chip
            icon={active ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            label={active ? 'Active' : 'Inactive'}
            size="small"
            color={active ? 'success' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      align: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Edit Category">
            <IconButton size="small" onClick={() => handleOpenEdit(params.row)} color="primary">
              <Edit size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Category">
            <IconButton size="small" onClick={() => setDeleteCategory(params.row)} color="error">
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      <PageHeader
        title="Expense Categories"
        subtitle="Manage category classifications for operational expenses."
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleOpenAdd}
            sx={{ fontWeight: 600, px: 3, textTransform: 'none' }}
          >
            Add Category
          </Button>
        }
      />

      {/* Filter and Search Bar - Full Width Responsive Layout */}
      <Box sx={{ width: '100%', p: 2.5, bgcolor: 'background.paper', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', width: '100%' }}>
          <TextField
            size="small"
            placeholder="Search category name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Showing <strong>{filteredCategories.length}</strong> of <strong>{(categories || []).length}</strong> categories
          </Typography>
        </Box>
      </Box>

      {/* Data Grid */}
      {error ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : isLoading ? (
        <Loader message="Loading expense categories..." />
      ) : (
        <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, overflow: 'hidden' }}>
          <DataGrid
            autoHeight
            rows={filteredCategories}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            disableRowSelectionOnClick
            sx={{
              border: 0,
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Box>
      )}

      {/* Create / Edit Modal */}
      <ExpenseCategoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitModal}
        loading={createMutation.isPending || updateMutation.isPending}
        category={editingCategory}
      />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={!!deleteCategory}
        onClose={() => setDeleteCategory(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Expense Category"
        message={`Are you sure you want to delete the category "${deleteCategory?.name}"?`}
        confirmText="Delete Category"
        confirmColor="error"
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
